import { useQuery } from "@tanstack/react-query";
import { request } from "@/lib/api";
import { useAuth } from "@/state/AuthContext";
import { isPaidUser } from "@/utils/roles";

/** Shared by the sidebar badge, the header badge, and the Inbox heading. */
export const UNREAD_NOTIFICATIONS_QUERY_KEY = ["notifications", "unread-count"] as const;

/**
 * One unread count for every surface. Sidebar, header, and Inbox used to poll
 * this endpoint on separate timers, so the badge and the heading could disagree.
 */
export function useUnreadNotifications() {
  const { user } = useAuth();
  const paid = Boolean(user && isPaidUser(user));
  const query = useQuery({
    queryKey: UNREAD_NOTIFICATIONS_QUERY_KEY,
    queryFn: async () => {
      const data = await request.get<{ count: number }>("/notifications/unread-count");
      return data.count;
    },
    enabled: paid,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });

  return {
    unreadCount: paid ? (query.data ?? 0) : 0,
    loading: query.isFetching,
    refresh: () => query.refetch(),
  };
}
